import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

// models
import { ITableSettingColumn, ITableSettingColumnsData } from '../../models/tableSettingColumn';
import { MeasurementUnit } from '../../models/measurementUnit';
import { ImperialDistanceUnit } from '../../models/measurementSystem';

type FormGroupType = {
	groups: FormArray<FormGroup<GroupsType>>;
}

type GroupsType = {
	selected: FormControl<boolean | null>; // null, if status indeterminate
	label: FormControl<string>;
	help_title: FormControl<string>;
	children?: FormArray<FormGroup<GroupsType>>;
	key: FormControl<string>;
	disabled: FormControl<boolean>;
}

type GroupValue = {
	selected?: boolean | null;
	label?: string;
	help_title?: string;
	children?: Array<GroupValue>;
	key?: string;
	measurement_unit?: MeasurementUnit;
	client_system_measure?: ImperialDistanceUnit;
	disabled?: boolean;
}

@Component({
    selector: 'app-columns-settings-modal',
    templateUrl: './columns-settings-modal.component.html',
    styleUrls: ['./columns-settings-modal.component.scss'],
    standalone: false
})
export class ColumnsSettingsModalComponent implements OnInit {

	formGroup: FormGroup<FormGroupType>;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: { allColumns: ITableSettingColumnsData, visibleColumns: Array<string> },
		private dialogRef: MatDialogRef<ColumnsSettingsModalComponent>,
		private formBuilder: FormBuilder
	) {}

	ngOnInit(): void {
		this.formGroup = this.formBuilder.group<FormGroupType>({
			groups: this.formBuilder.array<FormGroup<GroupsType>>(
				this.data.allColumns.columns.map((column: ITableSettingColumn) => this.createColumnGroup(column))
			)
		});
	}

	apply(): void {
		const columns: Array<string> = [],
			value: Array<GroupValue> = this.formGroup.controls.groups.value;

		const setColumn = (group: GroupValue): void => {
			if (group.children) {
				group.children.forEach((child: GroupValue) => setColumn(child))
			} else {
				if (group.selected || group.disabled) {
					columns.push(group.key);
				}
			}
		}

		value.forEach((group: GroupValue) => setColumn(group));

		this.dialogRef.close(columns);
	}

	checkGroupStatus(value: Array<GroupValue>, control: FormControl<boolean | null>): void {
		const enabledChildren = value.filter((child: GroupValue) => !child.disabled);
		
		if (enabledChildren.length === 0) {
			control.setValue(true, { emitEvent: false });
		} else if (enabledChildren.every((child: GroupValue) => child.selected)) {
			control.setValue(true, { emitEvent: false });
		} else if (enabledChildren.every((child: GroupValue) => !child.selected)) {
			control.setValue(false, { emitEvent: false });
		} else {
			control.setValue(null, { emitEvent: false });
		}
	}

	close(): void {
		this.dialogRef.close();
	}

	onCheckboxChange(event: any, checkbox: FormGroup<GroupsType>): void {
		if (checkbox.controls.disabled.value) {
			event.preventDefault();
			const originalValue = checkbox.controls.selected.value;
			checkbox.controls.selected.setValue(originalValue, { emitEvent: false });
		}
	}

	createColumnGroup(column: ITableSettingColumn): FormGroup<GroupsType> {
		const selectedControl: FormControl<boolean | null> = new FormControl<boolean>(false),
			labelControl: FormControl<string> = new FormControl<string>(column.title),
			keyControl: FormControl<string> = new FormControl<string>(column.key),
			helpTitleControl: FormControl<string> = new FormControl<string>(column.help_title),
			childrenControls: Array<FormGroup<GroupsType>> = column.children
				.map((child: ITableSettingColumn) => {
					let isSelected: boolean;
					if (child.selected !== undefined) {
						isSelected = child.selected;
					} else {
						isSelected = child.disabled ? false : this.data.visibleColumns.includes(child.key);
					}
					
					const childGroup = new FormGroup({
						selected: new FormControl<boolean | null>(isSelected),
						label: new FormControl<string>(child.title),
						help_title: new FormControl<string>(child.help_title),
						key: new FormControl<string>(child.key),
						disabled: new FormControl<boolean>(child.disabled || false)
					});

					if (child.disabled) {
						childGroup.controls.selected.disable();
					}
					
					return childGroup;
				}),
			childrenFormArray: FormArray<FormGroup<GroupsType>> = this.formBuilder.array<FormGroup<GroupsType>>(childrenControls);

		this.checkGroupStatus(childrenFormArray.value, selectedControl);

		selectedControl.valueChanges.subscribe((value: boolean) => {
			childrenControls.forEach((child: FormGroup<GroupsType>) => {
				if (!child.controls.disabled.value) {
					child.controls.selected.setValue(value, { emitEvent: false });
				}
			});
		});

		childrenFormArray.valueChanges.subscribe((value: Array<GroupValue>) => {
			this.checkGroupStatus(value, selectedControl);
		});

		return this.formBuilder.group<GroupsType>({
			selected: selectedControl,
			label: labelControl,
			key: keyControl,
			children: childrenFormArray,
			help_title: helpTitleControl,
			disabled: new FormControl<boolean>(column.disabled || false)
		});
	}
}
